import { FC, useMemo } from 'react'
import { TriagemRiscoBadge } from '../../../../components/TriagemRisco/TriagemRiscoBadge'
import { CondicaoCatalogo } from '../../formularios/core/condicao/catalogo'
import {
  DicionarioCondicao,
  NoDescricao,
  descreverCondicao,
} from '../../formularios/core/condicao/descreverCondicao'
import { CampoFormulario, RegraProtocolo } from '../core/_models'
import { dicionarioDeCampos } from '../../formularios/core/condicao/descreverCondicao'

type Props = {
  regras?: RegraProtocolo[]
  campos?: CampoFormulario[]
  catalogo?: CondicaoCatalogo
}

const ArvoreCondicao: FC<{ no: NoDescricao }> = ({ no }) => {
  if (no.tipo === 'item') {
    return <span>{no.texto}</span>
  }
  return (
    <>
      <span className='fw-bold'>{no.texto}</span>
      <ul className='mb-0'>
        {(no.filhos ?? []).map((filho, indice) => (
          <li key={indice}>
            <ArvoreCondicao no={filho} />
          </li>
        ))}
      </ul>
    </>
  )
}

/** Regras agrupadas por grupo, com a condição em pt-BR legível (T7a AC2). */
export const RegrasPorGrupo: FC<Props> = ({ regras, campos, catalogo }) => {
  const dicionario: DicionarioCondicao = useMemo(
    () => dicionarioDeCampos(campos ?? [], catalogo),
    [campos, catalogo]
  )

  const grupos = useMemo(() => {
    const mapa = new Map<string, RegraProtocolo[]>()
    ;(regras ?? []).forEach((regra) => {
      const chave = regra.grupo || 'Sem grupo'
      mapa.set(chave, [...(mapa.get(chave) ?? []), regra])
    })
    return Array.from(mapa.entries())
  }, [regras])

  if (grupos.length === 0) {
    return <p className='text-muted'>Esta versão ainda não tem regras.</p>
  }

  return (
    <div>
      {grupos.map(([grupo, regrasDoGrupo]) => (
        <section key={grupo} className='mb-8' aria-label={`Grupo ${grupo}`}>
          <h4 className='fw-bold mb-4'>{grupo}</h4>
          <div className='table-responsive'>
            <table className='table table-row-bordered align-middle'>
              <caption className='visually-hidden'>Regras do grupo {grupo}</caption>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th scope='col'>Código</th>
                  <th scope='col'>Condição</th>
                  <th scope='col'>Risco</th>
                  <th scope='col'>Justificativa e fonte</th>
                </tr>
              </thead>
              <tbody>
                {regrasDoGrupo.map((regra) => {
                  const descricao = descreverCondicao(regra.condicao, dicionario)
                  return (
                    <tr key={regra.codigo}>
                      <td>
                        <code>{regra.codigo}</code>
                      </td>
                      <td>
                        <ArvoreCondicao no={descricao.arvore} />
                        {descricao.avisos.length > 0 && (
                          <ul className='text-warning mt-2 mb-0'>
                            {descricao.avisos.map((aviso) => (
                              <li key={`${aviso.codigo}-${aviso.referencia}`}>
                                Atenção: {aviso.mensagem}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td>
                        <TriagemRiscoBadge cor={regra.cor} />
                      </td>
                      <td>
                        <div>{regra.justificativa}</div>
                        {regra.fonte && <small className='text-muted'>Fonte: {regra.fonte}</small>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  )
}
